/** Цифровой паспорт (КТМ-А). Полиморфен по `entityType`: изделие, техместо, … */

export const DOCUMENT_STATUSES = [
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'SIGNED',
  'REJECTED',
  'CANCELLED',
  'EXPIRED',
] as const;

export type KtmaDocumentStatus = (typeof DOCUMENT_STATUSES)[number];

/**
 * Связанный ЦП. В макете (`docs/ktma-ldemo/Актив.html`) связи показываются таблицей:
 * тип актива, заводской номер, наименование, статус, а у родительских — ещё тип связи.
 * Прийти может как объект, так и голый идентификатор.
 */
export type AssetLink = {
  id: string;
  name?: string;
  type?: string;
  factoryNumber?: string;
  status?: string;
  location?: string;
  /** «Входит в комплект», «Агрегат», «Технологическая» — из макета. */
  relation?: string;
};

/** Характеристика изделия: `assetProperties` приходит объектом `{ключ: значение}`. */
export type AssetProperty = {
  key: string;
  value: string;
  unit?: string;
};

/** Запись формуляра. Состав полей зависит от типа изделия, поэтому всё опционально. */
export type AssetEvent = {
  at: string;
  title: string;
  type?: string;
  location?: string;
  basis?: string;
  description?: string;
  responsible?: string;
  /**
   * ЭП записи формуляра, если бэк её прислал (`hasSignature`).
   * Нет поля — колонку не рисуем.
   */
  signature?: 'qualified' | 'unqualified' | 'absent';
};

/** Актив паспорта: DID выдаёт сервис, имя вводит пользователь. */
export type PassportAsset = {
  did: string;
  name: string;
};

export type PassportAttachment = {
  id: string;
  passportId: string;
  fileName: string;
  size: number;
  kind?: string;
  blobId: string;
  signatures: Array<{ id: string; fileName: string; size: number }>;
  /** Контракт сейчас отдаёт только `NOT_VERIFIED`: подпись криптографически не проверяется. */
  signatureVerification?: string;
  vcId?: string;
  createdAt: string;
};

/**
 * Карточка для UI. Собирается из `Document` OpenAPI: идентификаторы и `status`
 * лежат на документе, поля изделия — в `data`.
 *
 * Состав идентификационных полей — из US-01-04 и макета `Актив.html`:
 * критерий приёмки требует показывать `inventoryNumber`, `factoryNumber`,
 * `locationId` и `assetProperties`, помечая незаполненные.
 */
export type DigitalPassport = {
  id: string;
  number: string | number;
  name: string;
  entityType: string;
  /** Тип актива: СИ, КИПиА, МТР, Комплект… (макет: «Тип актива»). */
  type: string;
  manufacturer: string;
  factoryNumber: string | null;
  inventoryNumber?: string;
  /** Доменный статус документа из контракта (`DRAFT`, …), не исправность. */
  status: string;
  statusId?: string;
  /** Нет в OpenAPI — показываем, только если пришло в `data`. */
  lifecycleStatus?: string;
  pzp: string | null;
  /** Ссылка на техместо из контракта. */
  locationId?: string;
  /** Человекочитаемое место установки и его код (макет). */
  location?: string;
  locationCode?: string;
  /** Классификатор и артикул/тип изделия (макет). */
  class?: string;
  productNumber?: string;
  manufactureDate?: string;
  commissioningDate?: string;
  did?: string;
  vcId?: string;
  parentAssets: AssetLink[];
  childAssets: AssetLink[];
  assetProperties: AssetProperty[];
  eventLog: AssetEvent[];
  orgOwnerId?: string;
  createdAt?: string;
};

/** `POST /passports`. Обязателен только `entityType`. */
export type CreatePassportInput = {
  entityType: string;
  name?: string;
  type?: string;
  manufacturer?: string;
  factoryNumber?: string;
  inventoryNumber?: string;
  locationId?: string;
};

export type PassportListFilters = {
  search?: string;
  entityType?: string;
  status?: string;
  pzp?: string;
};

export type KtmaDocumentTemplate = {
  id: string;
  alias: string;
  title?: { ru?: string; en?: string } | string;
};

export type KtmaDocument = {
  id: string;
  templateId: string;
  alias?: string;
  documentNumber?: string;
  documentVersion?: number;
  status: string;
  data?: Record<string, unknown>;
  party1?: { id?: string };
  createdAt?: string;
  updatedAt?: string;
};

export type KtmaDocumentList = {
  items: KtmaDocument[];
  total: number;
  limit: number;
  offset: number;
};
